// ─── src/api/catalystPortal.ts ────────────────────────────────────────────────
//
// A **separate axios instance** for catalyst-scoped calls. It reads the token
// from sessionStorage every request rather than closing over it — that way a
// fresh login (or a logout in another tab) takes effect immediately, without
// having to rebuild the client.

import axios from "axios";

const SESSION_KEY = "gew_dashboard_user";

const BASE_URL =
  process.env.REACT_APP_API_BASE_URL ?? "https://api.startupsyria.org/api/v1";

export const catalystClient = axios.create({
  baseURL: BASE_URL,
  timeout: 10_000,
});

catalystClient.interceptors.request.use((config) => {
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    if (raw) {
      const user = JSON.parse(raw) as { accessToken?: string; role?: string };
      if (user.accessToken && user.role === "catalyst") {
        config.headers = config.headers ?? {};
        config.headers.Authorization = `Bearer ${user.accessToken}`;
      }
    }
  } catch {
    // sessionStorage inaccessible (e.g. private mode) — request goes without a token
  }
  return config;
});

// ── Auth ─────────────────────────────────────────────────────────────────────

export interface CatalystLoginResponse {
  accessToken: string;
  catalyst: {
    id: string;
    email: string;
    fullNameAr: string;
    governorate: string;
    cityArea: string;
  };
}

export const catalystLogin = async (
  email: string,
  password: string,
): Promise<CatalystLoginResponse> => {
  const { data } = await catalystClient.post<CatalystLoginResponse>(
    "/catalyst-portal/login",
    { email, password },
  );
  return data;
};

// ── Portal reads ─────────────────────────────────────────────────────────────

export interface CatalystEvent {
  id: string;
  proposalId: string;
  title: string;
  imageUrl: string;
  targetAudience: string;
  eventFormat: string;
  eventFormatOther?: string | null;
  motivation: string;
  agenda: string;
  structure: string;
  seriesDetails?: string | null;
  speakers?: string | null;
  eventDate: string;
  startTime: string;
  durationHours: string;
  venue: string;
  capacity: number;
  governorate: string;
  cityArea: string;
  hostFullNameAr: string;
  hostEmail: string;
  coHostName?: string | null;
  coHostEmail?: string | null;
  when: "upcoming" | "past";
  confirmedCount: number;
  waitlistCount: number;
  spotsRemaining: number;
  attendeeStatus: "Open" | "Waitlist" | "Completed";
}

/** Fields a catalyst can PATCH on their own meetup. Mirrors the backend DTO. */
export interface UpdateCatalystEventPayload {
  title?: string;
  imageUrl?: string;
  targetAudience?: string;
  eventFormat?: string;
  eventFormatOther?: string | null;
  motivation?: string;
  agenda?: string;
  structure?: string;
  seriesDetails?: string | null;
  speakers?: string | null;
  eventDate?: string;
  startTime?: string;
  durationHours?: string;
  venue?: string;
  capacity?: number;
  governorate?: string;
  cityArea?: string;
  coHostName?: string | null;
  coHostEmail?: string | null;
}

/**
 * Full proposal shape (the backend returns everything — the earlier slim
 * subset was insufficient for the edit modal's prefill).
 */
export interface CatalystProposal {
  id: string;
  hostEmail: string;
  hostFullNameAr: string;
  hostGender?: string | null;
  hostPhone: string;
  hostGovernorate: string;
  hostCityArea: string;
  coHostEmail?: string | null;
  coHostName?: string | null;
  additionalCoHosts?: string | null;
  eventTitle: string;
  targetAudience: string;
  eventFormat: string;
  eventFormatOther?: string | null;
  motivation: string;
  agenda: string;
  structure: string;
  seriesDetails?: string | null;
  speakers?: string | null;
  proposedDate: string;
  startTime: string;
  durationHours: string;
  venue: string;
  venueStatus: string;
  expectedAttendees: string;
  otherDetails?: string | null;
  partnerSupport: string;
  partnershipDetails: string;
  logisticsSupport?: string | null;
  wantsFacebookEvent: boolean;
  commitments: string[];
  status: string; // pending | accepted | rejected
  createdAt: string;
  updatedAt?: string;
}

export interface CatalystAttendee {
  id: string;
  eventId: string;
  fullName: string;
  gender: string;
  ageGroup: string;
  whatsapp: string;
  email: string;
  governorate: string;
  cityArea: string;
  currentStatus: string;
  skills: string[];
  linkedInUrl?: string | null;
  whyAttend: string;
  questionsForEvent?: string | null;
  status: "confirmed" | "waitlist";
  approvalStatus: "pending" | "accepted" | "rejected";
  checkedInAt?: string | null;
  /** Last time the GEW feedback form was emailed to this attendee. */
  feedbackSentAt?: string | null;
  /** When this attendee answered the GEW feedback form. */
  feedbackSubmittedAt?: string | null;
  createdAt: string;
}

export const getCatalystProposals = async () =>
  (await catalystClient.get<CatalystProposal[]>("/catalyst-portal/proposals")).data;

export const getCatalystEvents = async () =>
  (await catalystClient.get<CatalystEvent[]>("/catalyst-portal/events")).data;

export const getCatalystEventAttendees = async (
  eventId: string,
  filters: { status?: string; approvalStatus?: string } = {},
) => {
  const { data } = await catalystClient.get<CatalystAttendee[]>(
    `/catalyst-portal/events/${eventId}/attendees`,
    { params: filters },
  );
  return data;
};

// ── Portal writes ────────────────────────────────────────────────────────────

export const updateCatalystAttendeeApproval = async (id: string, accepted: boolean) => {
  await catalystClient.patch(`/catalyst-portal/attendees/${id}/approval`, { accepted });
};

export const updateCatalystEvent = async (
  eventId: string,
  payload: UpdateCatalystEventPayload,
): Promise<CatalystEvent> => {
  const { data } = await catalystClient.patch<CatalystEvent>(
    `/catalyst-portal/events/${eventId}`,
    payload,
  );
  return data;
};

/**
 * Catalyst edit for THEIR OWN pending proposal. Same payload shape the
 * admin uses (mirrors the backend UpdateMeetupProposalDto). Backend
 * enforces both ownership (host/co-host email) and "pending only".
 */
export interface UpdateCatalystProposalPayload {
  eventTitle?: string;
  targetAudience?: string;
  eventFormat?: string;
  eventFormatOther?: string;
  motivation?: string;
  agenda?: string;
  structure?: string;
  seriesDetails?: string;
  speakers?: string;
  proposedDate?: string;
  startTime?: string;
  durationHours?: string;
  venue?: string;
  venueStatus?: string;
  expectedAttendees?: string;
  otherDetails?: string;
  hostGovernorate?: string;
  hostCityArea?: string;
  coHostName?: string;
  coHostEmail?: string;
  additionalCoHosts?: string;
  partnerSupport?: string;
  partnershipDetails?: string;
  logisticsSupport?: string;
  wantsFacebookEvent?: boolean;
}

export const updateCatalystProposal = async (
  proposalId: string,
  payload: UpdateCatalystProposalPayload,
): Promise<CatalystProposal> => {
  const { data } = await catalystClient.patch<CatalystProposal>(
    `/catalyst-portal/proposals/${proposalId}`,
    payload,
  );
  return data;
};

export interface CatalystCheckInResponse {
  result: "ok" | "already_checked_in" | "not_accepted" | "wrong_event" | "not_found";
  message: string;
  attendee?: {
    id: string;
    fullName: string;
    email: string;
    status: string;
    approvalStatus: string;
    checkedInAt?: string | null;
  };
  event?: { id: string; title: string; eventDate: string };
}

export const catalystCheckInByToken = async (token: string, eventId?: string) => {
  const { data } = await catalystClient.post<CatalystCheckInResponse>(
    "/catalyst-portal/attendees/check-in",
    { token, ...(eventId ? { eventId } : {}) },
  );
  return data;
};

export const catalystCheckInById = async (id: string) => {
  const { data } = await catalystClient.post<CatalystCheckInResponse>(
    `/catalyst-portal/attendees/${id}/check-in`,
  );
  return data;
};

export const catalystUndoCheckIn = async (id: string) => {
  await catalystClient.delete(`/catalyst-portal/attendees/${id}/check-in`);
};
